import type { BootstrapSeed } from '@/types/bootstrap';

/** Successful parse — the normalized seed array. */
interface ValidOk {
  ok: true;
  edges: BootstrapSeed[];
}

/** Failed parse — a human-readable reason for the inline error. */
interface ValidErr {
  ok: false;
  error: string;
}

export type BootstrapValidation = ValidOk | ValidErr;

const REQUIRED_STRING_FIELDS = ['remote', 'uuid', 'publicKey', 'serverName'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parse + validate the JSON textarea into a `BootstrapSeed[]`.
 *
 * Client-side mirror of the backend `BootstrapSeedDto` rules: the body must be
 * a JSON array; every element needs non-empty `remote`, `uuid`, `publicKey`,
 * `serverName`; `port`/`priority` must be integers when present. The backend
 * re-validates authoritatively — this is a fast-feedback guard so the operator
 * fixes typos before the write fires.
 */
export function validateBootstrapPool(raw: string): BootstrapValidation {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, edges: [] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { ok: false, error: `Invalid JSON: ${err instanceof Error ? err.message : 'parse error'}` };
  }

  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'The pool must be a JSON array of edge objects.' };
  }

  const edges: BootstrapSeed[] = [];

  for (let i = 0; i < parsed.length; i += 1) {
    const entry = parsed[i];
    const at = `Edge #${i + 1}`;

    if (!isRecord(entry)) {
      return { ok: false, error: `${at}: each edge must be an object.` };
    }

    for (const field of REQUIRED_STRING_FIELDS) {
      const value = entry[field];
      if (typeof value !== 'string' || value.trim() === '') {
        return { ok: false, error: `${at}: "${field}" is required and must be a non-empty string.` };
      }
    }

    if (entry.port !== undefined && !Number.isInteger(entry.port)) {
      return { ok: false, error: `${at}: "port" must be an integer.` };
    }
    if (entry.priority !== undefined && !Number.isInteger(entry.priority)) {
      return { ok: false, error: `${at}: "priority" must be an integer.` };
    }

    edges.push(entry as unknown as BootstrapSeed);
  }

  return { ok: true, edges };
}

/** Pretty-print a pool for the textarea (stable 2-space indent). */
export function formatBootstrapPool(edges: BootstrapSeed[]): string {
  return JSON.stringify(edges, null, 2);
}
